import { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import type { ReactNode } from 'react';
import { getIntl } from '@umijs/max';
import { message } from 'antd';
import type { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { login as loginRequest, fetchCurrentUser, refreshToken as refreshTokenRequest } from '@/services/korsmet/auth';
import { apiClient, extractErrorMessage, setAccessToken } from '@/services/apiClient';
import type { AuthResponse, Tokens, User } from '@/types';
import {
  loadAuthState,
  loadAccounts,
  loadActiveUserId,
  markAccountExpired,
  persistAuthState,
  removeAccount,
  setActiveAccount,
  type PersistedAccount,
} from '@/utils/authStorage';

interface AuthContextValue {
  user: User | null;
  tokens: Tokens | null;
  isLoading: boolean;
  /** 浏览器里同时保持登录的全部账号（Google 式多账号）。 */
  accounts: PersistedAccount[];
  login: (username: string, password: string) => Promise<void>;
  /** 退出**当前**账号；其余账号仍在，返回接替的那个，没有则 null。 */
  logout: () => PersistedAccount | null;
  switchAccount: (userId: number) => PersistedAccount | null;
  /** 用指定账号的 refresh token 换一套新令牌。换不动就把该账号踢掉并返回 null。 */
  refreshAccount: (userId: number) => Promise<PersistedAccount | null>;
  refreshUser: () => Promise<User | null>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [tokens, setTokens] = useState<Tokens | null>(null);
  const [accounts, setAccounts] = useState<PersistedAccount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const isRefreshingRef = useRef(false);
  const failedQueueRef = useRef<Array<{
    resolve: (value?: unknown) => void;
    reject: (reason?: unknown) => void;
  }>>([]);

  useEffect(() => {
    const restoreSession = async () => {
      const persisted = loadAuthState();
      if (!persisted?.tokens?.accessToken) {
        setIsLoading(false);
        return;
      }

      // 先设置 token 到拦截器，确保后续请求能带上 Authorization header
      setAccessToken(persisted.tokens.accessToken);

      // 然后更新 state
      setAccounts(loadAccounts());
      setTokens(persisted.tokens);
      if (persisted.user) {
        setUser(persisted.user);
        setIsLoading(false);
        return;
      }

      // 如果没有缓存的用户信息，则调用 API 获取
      try {
        const me = await fetchCurrentUser();
        setUser(me);
        persistAuthState({ tokens: persisted.tokens, user: me });
        setAccounts(loadAccounts());
      } catch (_error) {
        void message.warning(getIntl().formatMessage({ id: 'app.auth.sessionExpired', defaultMessage: '登录状态已过期，请重新登录' }));
        // 只清掉失效的这个账号，别把其他还好好的账号一起踢掉。
        // 走到这里说明缓存里没有 user 信息，只能按「当前账号」定位。
        const next = removeAccount(loadActiveUserId());
        setAccounts(loadAccounts());
        setTokens(next?.tokens ?? null);
        setUser(next?.user ?? null);
        setAccessToken(next?.tokens.accessToken ?? null);
      } finally {
        setIsLoading(false);
      }
    };

    void restoreSession();
  }, []);

  useEffect(() => {
    if (tokens?.accessToken) {
      setAccessToken(tokens.accessToken);
    } else {
      setAccessToken(null);
    }
  }, [tokens]);

  const persistState = useCallback((authResponse: AuthResponse) => {
    persistAuthState({ tokens: authResponse.tokens, user: authResponse.user });
    setAccounts(loadAccounts());
    setTokens(authResponse.tokens);
    setUser(authResponse.user);
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    setIsLoading(true);
    try {
      const authResponse = await loginRequest({ username, password });
      persistState(authResponse);
      setAccessToken(authResponse.tokens.accessToken);
      void message.success(getIntl().formatMessage({ id: 'app.auth.loginSuccess', defaultMessage: '登录成功' }));
    } catch (error) {
      void message.error(extractErrorMessage(error));
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [persistState]);

  const applyAccount = useCallback((account: PersistedAccount | null) => {
    setAccounts(loadAccounts());
    setTokens(account?.tokens ?? null);
    setUser(account?.user ?? null);
    setAccessToken(account?.tokens.accessToken ?? null);
    return account;
  }, []);

  const logout = useCallback(() => {
    const next = applyAccount(removeAccount(user?.id ?? null));
    const intl = getIntl();

    if (next) {
      void message.info(intl.formatMessage(
        { id: 'app.auth.switchedAccount', defaultMessage: '已退出该账号，当前账号：{name}' },
        { name: next.user?.fullName ?? next.user?.username ?? '' },
      ));
    } else {
      void message.info(intl.formatMessage({ id: 'app.auth.loggedOut', defaultMessage: '已退出登录' }));
    }

    return next;
  }, [applyAccount, user?.id]);

  const switchAccount = useCallback((userId: number) => {
    setActiveAccount(userId);
    const target = loadAccounts().find((item) => item.user?.id === userId) ?? null;
    return target ? applyAccount(target) : null;
  }, [applyAccount]);

  /**
   * 单独刷新某个账号的令牌。
   *
   * 多账号场景下，非当前账号的 access token 会在后台默默过期：切过去用它发请求
   * 必然 401，而 401 又把人弹回登录页 —— 用户再选同一个账号，同一张过期票再来一次，
   * 就死循环了。所以在用之前先拿它自己的 refresh token 换一张新的。
   *
   * refresh 也失效说明这个账号的会话真没了 —— 但不删掉它：用户仍然认得这个账号，
   * 只是需要重新输密码。标记成 expired，列表里照常显示。
   */
  const refreshAccount = useCallback(async (userId: number) => {
    const account = loadAccounts().find((item) => item.user?.id === userId);
    if (!account?.tokens?.refreshToken) {
      return null;
    }

    try {
      const authResponse = await refreshTokenRequest(account.tokens.refreshToken);
      persistAuthState({ tokens: authResponse.tokens, user: authResponse.user });
      const refreshed = loadAccounts().find((item) => item.user?.id === authResponse.user.id)
        ?? null;
      applyAccount(refreshed);
      return refreshed;
    } catch (_error) {
      markAccountExpired(userId);
      setAccounts(loadAccounts());
      return null;
    }
  }, [applyAccount]);

  const refreshUser = useCallback(async (): Promise<User | null> => {
    if (!tokens) {
      return null;
    }
    try {
      const me = await fetchCurrentUser();
      setUser(me);
      persistAuthState({ tokens, user: me });
      return me;
    } catch (error) {
      void message.error(extractErrorMessage(error));
      const axiosError = error as AxiosError;
      if (axiosError?.response?.status === 401) {
        logout();
      }
      throw error;
    }
  }, [tokens, logout]);

  const processQueue = useCallback((error: unknown = null) => {
    failedQueueRef.current.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve();
      }
    });
    failedQueueRef.current = [];
  }, []);

  const refreshAuthToken = useCallback(async (): Promise<string | null> => {
    if (!tokens?.refreshToken) {
      return null;
    }

    try {
      const authResponse = await refreshTokenRequest(tokens.refreshToken);
      persistState(authResponse);
      setAccessToken(authResponse.tokens.accessToken);
      processQueue();
      return authResponse.tokens.accessToken;
    } catch (error) {
      processQueue(error);
      logout();
      return null;
    }
  }, [tokens?.refreshToken, persistState, processQueue, logout]);

  useEffect(() => {
    const interceptorId = apiClient.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { retryAttempted?: boolean };

        if (error.response?.status === 401 && !originalRequest.retryAttempted) {
          if (isRefreshingRef.current) {
            return new Promise((resolve, reject) => {
              failedQueueRef.current.push({ resolve, reject });
            })
              .then(() => apiClient(originalRequest))
              .catch((err) => Promise.reject(err));
          }

          originalRequest.retryAttempted = true;
          isRefreshingRef.current = true;

          try {
            const newAccessToken = await refreshAuthToken();
            isRefreshingRef.current = false;

            if (newAccessToken && originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
              return await apiClient(originalRequest);
            }
          } catch (refreshError) {
            isRefreshingRef.current = false;
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      },
    );

    return () => {
      apiClient.interceptors.response.eject(interceptorId);
    };
  }, [refreshAuthToken]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      tokens,
      isLoading,
      accounts,
      login,
      logout,
      switchAccount,
      refreshAccount,
      refreshUser,
    }),
    [user, tokens, isLoading, accounts, login, logout, switchAccount, refreshAccount, refreshUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
